import React, { useEffect } from 'react';
import {
    Box,
    Container,
    Card,
    CardContent,
    Typography,
    Button,
    CircularProgress,
    LinearProgress,
    Alert,
} from '@mui/material';
import { Login as LoginIcon } from '@mui/icons-material';
import { useI18nContext } from '@/i18n/i18n-react';
import { styleHelpers } from '@/theme';

const LoginPage: React.FC = () => {
    const { LL } = useI18nContext();
    const [isRedirecting, setIsRedirecting] = React.useState(false);
    const [countdown, setCountdown] = React.useState(2);
    const params = new URLSearchParams(window.location.search);
    const hasError = params.has('error');
    const manual = hasError || params.has('manual') || params.has('logged_out');

    const redirectToLogin = () => {
        setIsRedirecting(true);
        const redirectPath = new URLSearchParams(window.location.search).get('redirect') || '/';
        window.location.href = `/oidc?redirect=${encodeURIComponent(redirectPath)}`;
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.has('error') || params.has('manual') || params.has('logged_out')) return;

        // Start countdown for auto-redirect
        const countdownInterval = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(countdownInterval);
                    redirectToLogin();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(countdownInterval);
    }, []);

    const handleLogin = () => {
        redirectToLogin();
    };

    return (
        <Box
            sx={(theme) => ({
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
                color: theme.brand.onBrand,
                background: theme.brand.gradients.primary,
                position: 'relative',
                overflow: 'hidden',
                // Texture, plus a bright bloom so the backdrop has depth rather
                // than reading as one flat gradient.
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: theme.brand.patterns.dots,
                    opacity: 0.14,
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background:
                        'radial-gradient(760px 420px at 50% 8%, rgba(255,255,255,0.20), transparent 62%)',
                    pointerEvents: 'none',
                },
            })}
        >
            <Container maxWidth="sm">
                <Card
                    sx={(theme) => ({
                        position: 'relative',
                        zIndex: 1,
                        borderRadius: 5,
                        color: 'text.primary',
                        border: theme.brand.borders.hairlineStrong,
                        backgroundColor: theme.brand.backgrounds.glass,
                        backdropFilter: 'blur(18px)',
                        boxShadow: theme.brand.effects.shadowLift,
                    })}
                >
                    <CardContent sx={{ p: { xs: 3.5, sm: 5 }, textAlign: 'center' }}>
                        <Box
                            sx={{
                                ...styleHelpers.iconBox(80),
                                mx: 'auto',
                                mb: 3,
                            }}
                        >
                            <LoginIcon sx={{ fontSize: 40 }} />
                        </Box>

                        <Typography
                            variant="h3"
                            sx={{ fontSize: { xs: '2rem', sm: '2.4rem' }, mb: 1.5 }}
                        >
                            {LL.appTitle()}
                        </Typography>
                        <Box
                            sx={(theme) => ({
                                width: 64,
                                height: 4,
                                mx: 'auto',
                                mb: 2.5,
                                borderRadius: 999,
                                background: theme.brand.gradients.accent,
                            })}
                        />

                        <Typography variant="body1" component="p" sx={{
                            color: "text.secondary"
                        }}>
                            {LL.loginRequired()}
                        </Typography>

                        {hasError && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {LL.loginFailed()}
                            </Alert>
                        )}
                        {!manual && !isRedirecting && countdown > 0 && (
                            <>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "text.secondary",
                                        mb: 2
                                    }}>
                                    {LL.redirectingIn({ countdown })}
                                </Typography>
                                <LinearProgress sx={{ mb: 2 }} />
                            </>
                        )}

                        <Button
                            variant="contained"
                            size="large"
                            fullWidth
                            onClick={handleLogin}
                            disabled={isRedirecting}
                            startIcon={
                                isRedirecting ? <CircularProgress size={20} /> : <LoginIcon />
                            }
                            sx={{ mt: 3, py: 1.25 }}
                        >
                            {isRedirecting ? LL.loggingIn() : LL.loginWithOIDC()}
                        </Button>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default LoginPage;
